import React from "react";

export interface QuarterCounterProps {
  title: string;
  quarterStart: string; // e.g., "01/01/26"
  quarterEnd: string;   // e.g., "03/31/26"
  loading: boolean;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const parseMMDDYY = (dateStr: string): Date => {
  const [month, day, yearStr] = dateStr.split("/").map(Number);
  let year = yearStr < 100 ? yearStr + 2000 : yearStr;
  return new Date(Date.UTC(year, month - 1, day));
};

const toUTCDay = (date: Date): number => Math.floor(date.getTime() / MS_PER_DAY);

const QuarterCounterCard: React.FC<QuarterCounterProps> = ({
  title,
  quarterStart,
  quarterEnd,
  loading,
}) => {
  const startDate = parseMMDDYY(quarterStart);
  const endDate = parseMMDDYY(quarterEnd);
  const today = new Date();
  const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const cardBase =
  "max-w-xs p-4 rounded-md shadow bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700";


  const totalDays = toUTCDay(endDate) - toUTCDay(startDate) + 1;
  const daysLeftRaw = toUTCDay(endDate) - toUTCDay(todayUTC);

  let daysLeft = Math.max(0, daysLeftRaw);
  let percentComplete = 0;
  let statusText = "";

  if (daysLeftRaw < 0) {
    daysLeft = 0;
    percentComplete = 1;
    statusText = "Quarter completed";
  } else if (daysLeftRaw >= totalDays) {
    percentComplete = 0;
    statusText = `Quarter starts in ${daysLeftRaw} days`;
  } else {
    percentComplete = (totalDays - daysLeft) / totalDays;
    statusText = `Days left in quarter: ${daysLeft}`;
  }

  if (loading) {
    return (
      <div className={`${cardBase} text-center`}>
        <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">{title}</h3>
        <div className="mt-4">
          <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading Quarter Counter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cardBase}>
      <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">
        {title}
      </h3>

      <hr className="my-2 border-gray-200 dark:border-gray-700" />

      <p className="text-gray-800 dark:text-gray-200">
        {statusText}
      </p>

      <p className="text-gray-700 dark:text-gray-300">
        Total days in quarter: {totalDays}
      </p>

      {/* Progress bar background */}
      <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded mt-2 overflow-hidden">
        {/* Progress fill */}
        <div
          className="h-4 bg-blue-500 dark:bg-blue-400 rounded"
          style={{ width: `${(percentComplete * 100).toFixed(0)}%` }}
        />
      </div>
    </div>
  );
};

export default QuarterCounterCard;